import * as Yup from 'yup';
import { TemplateFieldTypeEnum } from '@/__generated__/graphql';
import type { QaqcKpiTemplate, QaqcKpiTemplateField } from '../types';
import { buildFieldByKey } from './utils';

export const QAQC_NUMERIC_HELPER =
  'Use digits only. One decimal point is allowed. Negative numbers may start with - (e.g. -12 or -3.5).';

export const QAQC_TIME_HELPER = 'Use time (HH:MM).';

const requiredMessage = (field: QaqcKpiTemplateField) => `${field.name} is required`;

export const isTimeLikeInput = (fieldKey: string, field: QaqcKpiTemplateField) =>
  fieldKey.includes('time') && field.type === TemplateFieldTypeEnum.Input;

/** True if empty; otherwise must be a finite number string (allows leading '-'; no lone '.' or '-.'). */
export const isValidQaqcNumericString = (value: string | undefined): boolean => {
  if (value === undefined || value === '') {
    return true;
  }
  if (value === '.' || value === '-.' || value === '-') {
    return false;
  }
  if (!/^-?\d*\.?\d*$/.test(value)) {
    return false;
  }
  return Number.isFinite(Number(value));
};

const numericFormatMessage = 'Enter a valid number (digits and optional decimal only).';

const isNumericLikeType = (type: TemplateFieldTypeEnum) =>
  type === TemplateFieldTypeEnum.Numeric ||
  type === TemplateFieldTypeEnum.MeasurementPaintArea ||
  type === TemplateFieldTypeEnum.MeasurementPaintLength ||
  type === TemplateFieldTypeEnum.MeasurementPaintDiameter;

export const getQaqcFieldFormatHint = (
  field: QaqcKpiTemplateField,
  fieldKey: string
): string | undefined => {
  if (field.isDerived) {
    return undefined;
  }
  const multi = field.allowsMultipleValues || field.type === TemplateFieldTypeEnum.MultiSelect;
  if (multi) {
    if (field.type === TemplateFieldTypeEnum.Numeric) {
      return QAQC_NUMERIC_HELPER;
    }
    if (isTimeLikeInput(fieldKey, field)) {
      return QAQC_TIME_HELPER;
    }
    return undefined;
  }
  if (isNumericLikeType(field.type)) {
    return QAQC_NUMERIC_HELPER;
  }
  if (isTimeLikeInput(fieldKey, field)) {
    return QAQC_TIME_HELPER;
  }
  return undefined;
};

const buildScalarNumericSchema = (field: QaqcKpiTemplateField) => {
  let schema = Yup.string().test('numeric-format', numericFormatMessage, (value) =>
    isValidQaqcNumericString(value ?? '')
  );
  if (field.isRequired) {
    schema = schema.required(requiredMessage(field));
  }
  return schema;
};

const buildMultiNumericSchema = (field: QaqcKpiTemplateField) => {
  const inner = Yup.string().test(
    'numeric-format',
    numericFormatMessage,
    (value) => value !== undefined && value !== '' && isValidQaqcNumericString(value)
  );
  let schema = Yup.array().of(inner);
  if (field.isRequired) {
    schema = schema.min(1, requiredMessage(field));
  }
  return schema;
};

const buildMultiMultiSelectSchema = (field: QaqcKpiTemplateField) => {
  const options = field.options ?? [];
  const inner = Yup.string().oneOf(options, 'Each value must be one of the listed options.');
  let schema = Yup.array().of(inner);
  if (field.isRequired) {
    schema = schema.min(1, requiredMessage(field));
  }
  return schema;
};

const buildMultiFreeTextSchema = (field: QaqcKpiTemplateField) => {
  let schema = Yup.array().of(Yup.string());
  if (field.isRequired) {
    schema = schema.min(1, requiredMessage(field));
  }
  return schema;
};

const buildTimeInputSchema = (field: QaqcKpiTemplateField) => {
  const timePattern = /^([01]?\d|2[0-3]):[0-5]\d$/;
  let schema = Yup.string().test('time-format', 'Enter a valid time (HH:MM).', (value) => {
    if (value === undefined || value === '') {
      return true;
    }
    return timePattern.test(value);
  });
  if (field.isRequired) {
    schema = schema.required(requiredMessage(field));
  }
  return schema;
};

const buildGenericTextSchema = (field: QaqcKpiTemplateField) => {
  let schema = Yup.string();
  if (field.isRequired) {
    schema = schema.required(requiredMessage(field));
  }
  return schema;
};

const buildSingleSelectSchema = (field: QaqcKpiTemplateField) => {
  const options = field.options ?? [];
  if (field.isRequired) {
    return Yup.string().required(requiredMessage(field)).oneOf(options, 'Select a valid option.');
  }
  return Yup.string().oneOf([...options, ''], 'Select a valid option.');
};

const buildMultiValueSchema = (field: QaqcKpiTemplateField, fieldKey: string) => {
  if (field.type === TemplateFieldTypeEnum.MultiSelect) {
    return buildMultiMultiSelectSchema(field);
  }
  if (field.type === TemplateFieldTypeEnum.Numeric) {
    return buildMultiNumericSchema(field);
  }
  if (isTimeLikeInput(fieldKey, field)) {
    const timePattern = /^([01]?\d|2[0-3]):[0-5]\d$/;
    const inner = Yup.string().test(
      'time-format',
      'Each entry must be a valid time (HH:MM).',
      (value) => value !== undefined && value !== '' && timePattern.test(value)
    );
    let schema = Yup.array().of(inner);
    if (field.isRequired) {
      schema = schema.min(1, requiredMessage(field));
    }
    return schema;
  }
  return buildMultiFreeTextSchema(field);
};

const buildScalarSchema = (field: QaqcKpiTemplateField, fieldKey: string) => {
  if (field.type === TemplateFieldTypeEnum.SingleSelect && field.options?.length) {
    return buildSingleSelectSchema(field);
  }

  if (isNumericLikeType(field.type)) {
    return buildScalarNumericSchema(field);
  }

  if (field.type === TemplateFieldTypeEnum.Input) {
    if (isTimeLikeInput(fieldKey, field)) {
      return buildTimeInputSchema(field);
    }
    return buildGenericTextSchema(field);
  }

  if (field.type === TemplateFieldTypeEnum.Textarea) {
    return buildGenericTextSchema(field);
  }

  if (field.type === TemplateFieldTypeEnum.Date) {
    let schema = Yup.string().matches(/^\d{4}-\d{2}-\d{2}$/, {
      message: 'Use a valid date (YYYY-MM-DD).',
      excludeEmptyString: true,
    });
    if (field.isRequired) {
      schema = schema.required(requiredMessage(field));
    }
    return schema;
  }

  return buildGenericTextSchema(field);
};

const valuesShapeForTable = (
  fieldKeys: string[],
  fieldByKey: Map<string, QaqcKpiTemplateField>
) => {
  const shape: Record<string, Yup.AnySchema> = {};
  for (const key of fieldKeys) {
    const field = fieldByKey.get(key);
    if (!field || field.isDerived) {
      continue;
    }
    if (field.allowsMultipleValues || field.type === TemplateFieldTypeEnum.MultiSelect) {
      shape[key] = buildMultiValueSchema(field, key);
    } else if (field.type === TemplateFieldTypeEnum.SingleSelect && field.options?.length) {
      shape[key] = buildSingleSelectSchema(field);
    } else {
      shape[key] = buildScalarSchema(field, key);
    }
  }
  return shape;
};

/**
 * Validates QA/QC table entry values by template field type (numeric, text, time, select, etc.).
 * Table order matches `buildEmptyQaqcFormValues` (sections and tables sorted by `order`).
 */
export const buildQaqcFormValidationSchema = (template: QaqcKpiTemplate | undefined) => {
  if (!template?.qaqcConfig?.length) {
    return Yup.object({
      tables: Yup.array(),
    });
  }

  const fieldByKey = buildFieldByKey(template);
  const sortedSections = [...template.qaqcConfig].sort((a, b) => a.order - b.order);

  const tableSchemas: Yup.ObjectSchema<Record<string, unknown>>[] = [];

  for (const section of sortedSections) {
    const sortedTables = [...section.tables].sort((a, b) => a.order - b.order);
    for (const table of sortedTables) {
      const fieldKeys = table.fieldKeys ?? [];
      const valueShape = valuesShapeForTable(fieldKeys, fieldByKey);
      tableSchemas.push(
        Yup.object({
          sectionKey: Yup.string(),
          tableKey: Yup.string(),
          title: Yup.string(),
          supportsMultipleEntries: Yup.boolean(),
          fieldKeys: Yup.array().of(Yup.string()),
          entries: Yup.array().of(
            Yup.object({
              entryIndex: Yup.number(),
              values: Yup.object().shape(valueShape),
            })
          ),
        })
      );
    }
  }

  if (tableSchemas.length === 0) {
    return Yup.object({
      tables: Yup.array(),
    });
  }

  const tupleSchema = Yup.tuple(
    tableSchemas as [
      Yup.ObjectSchema<Record<string, unknown>>,
      ...Yup.ObjectSchema<Record<string, unknown>>[]
    ]
  );

  return Yup.object({
    tables: tupleSchema,
  });
};
